'use client';

import { useState, useRef, useEffect, Fragment } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { getBlogLanguageSwitchUrl, isOnBlogPage, isBlogPostPage, isFinnishPage, HOME_EN, HOME_FI, ABOUT_EN, ABOUT_FI, TEAM_EN, TEAM_FI, CONTACT_EN, CONTACT_FI, BLOG_EN, BLOG_FI, WORK_EN, WORK_FI, CAREERS_EN, CAREERS_FI, SERVICES_EN, SERVICES_FI, PRIVACY_EN, PRIVACY_FI } from '../../lib/blogPaths';
import { useLanguageSwitcher } from '../../contexts/LanguageSwitcherContext';

/** Main nav links. Intentionally hardcoded here (not from WP menu) so structure and labels are controlled in code. */
const navLinks = [
  {
    label: 'Services',
    href: '/services',
    children: [
      { label: 'Software Development', href: '/services/software-development' },
      { label: 'UI/UX design', href: '/services/ui-ux-design' },
    ],
  },
  { label: 'Our Work', href: '/work' },
  { label: 'About', href: '/about' },
  { label: 'Careers', href: '/careers' },
  { label: 'Team', href: '/team' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

const HOVER_OPEN_DELAY_MS = 150;

// Mobile menu animation – staggered reveal (Reaktor-inspired)
const mobilePanelVariants = {
  open: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.28, ease: [0.32, 0.72, 0, 1] },
  },
  closed: {
    opacity: 0,
    y: -12,
    scale: 0.98,
    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
  },
};

const mobileNavVariants = {
  open: {
    transition: { staggerChildren: 0.048, delayChildren: 0.08 },
  },
  closed: {
    transition: { staggerChildren: 0.028, staggerDirection: -1 },
  },
};

const mobileItemVariants = {
  open: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: [0.32, 0.72, 0, 1] },
  },
  closed: {
    opacity: 0,
    y: 14,
    transition: { duration: 0.18 },
  },
};

/** True when the current path matches this nav link (exact or under a path like /services/...). */
function isNavItemActive(asPath, href) {
  if (!asPath || !href) return false;
  if (asPath === href) return true;
  if (href !== '/' && asPath.startsWith(href + '/')) return true;
  return false;
}

export function Header() {
  const router = useRouter();
  const isFrontPage = router.pathname === '/' || router.pathname === HOME_FI || router.pathname === HOME_EN;
  const isBlogPost = isBlogPostPage(router.pathname);
  const isCareersPage =
    router.pathname === '/careers' ||
    router.pathname === CAREERS_FI ||
    router.pathname === '/rekry/[slug]' ||
    router.pathname === '/careers/[slug]';
  const isAboutPage = router.asPath === ABOUT_EN || router.asPath === ABOUT_FI;
  const isWorkPage = router.pathname === '/work' || router.pathname === WORK_FI;
  const isTeamPage = router.pathname === '/team' || router.pathname === TEAM_FI;
  const isContactPage = router.pathname === '/contact' || router.pathname === CONTACT_FI;
  const isServicesPage = router.pathname === '/services' || router.pathname === SERVICES_FI;
  const isSoftwareDevelopmentPage = router.pathname === '/services/software-development' || router.pathname === '/palvelut/ohjelmistokehitys';
  const isUiUxDesignPage = router.pathname === '/services/ui-ux-design' || router.pathname === '/palvelut/ui-ux-suunnittelu';
  const useDarkHeader = isFrontPage || isBlogPost || isCareersPage || isAboutPage || isWorkPage || isTeamPage || isContactPage || isServicesPage || isSoftwareDevelopmentPage || isUiUxDesignPage;
  const onBlog = isOnBlogPage(router.pathname);
  const isAboutRoute = router.pathname === '/[aboutSlug]';
  const isFinnish =
    isFinnishPage(router.pathname) || (isAboutRoute && router.asPath === ABOUT_FI);
  const aboutNavLabel = isFinnish ? 'Meistä' : 'About';
  const aboutNavHref = isFinnish ? ABOUT_FI : ABOUT_EN;
  const teamNavLabel = isFinnish ? 'Tiimi' : 'Team';
  const teamNavHref = isFinnish ? TEAM_FI : TEAM_EN;
  const contactNavLabel = isFinnish ? 'Yhteys' : 'Contact';
  const contactNavHref = isFinnish ? CONTACT_FI : CONTACT_EN;
  const blogNavLabel = isFinnish ? 'Blogi' : 'Blog';
  const blogNavHref = isFinnish ? BLOG_FI : BLOG_EN;
  const workNavLabel = isFinnish ? 'Työmme' : 'Our Work';
  const workNavHref = isFinnish ? WORK_FI : WORK_EN;
  const careersNavLabel = isFinnish ? 'Rekry' : 'Careers';
  const careersNavHref = isFinnish ? CAREERS_FI : CAREERS_EN;
  const servicesNavLabel = isFinnish ? 'Palvelut' : 'Services';
  const servicesNavHref = isFinnish ? SERVICES_FI : SERVICES_EN;
  const getNavHref = (href) => {
    if (href === '/about') return aboutNavHref;
    if (href === '/team') return teamNavHref;
    if (href === '/contact') return contactNavHref;
    if (href === '/blog') return blogNavHref;
    if (href === '/work') return workNavHref;
    if (href === '/careers') return careersNavHref;
    if (href === '/services') return servicesNavHref;
    if (href === '/services/software-development') return isFinnish ? `${SERVICES_FI}/ohjelmistokehitys` : `${SERVICES_EN}/software-development`;
    if (href === '/services/ui-ux-design') return isFinnish ? `${SERVICES_FI}/ui-ux-suunnittelu` : `${SERVICES_EN}/ui-ux-design`;
    return href;
  };
  const getNavLabel = (href, label) => {
    if (href === '/about') return aboutNavLabel;
    if (href === '/team') return teamNavLabel;
    if (href === '/contact') return contactNavLabel;
    if (href === '/blog') return blogNavLabel;
    if (href === '/work') return workNavLabel;
    if (href === '/careers') return careersNavLabel;
    if (href === '/services') return servicesNavLabel;
    if (href === '/services/software-development') return isFinnish ? 'Ohjelmistokehitys' : label;
    if (href === '/services/ui-ux-design') return isFinnish ? 'UI/UX-suunnittelu' : label;
    return label;
  };
  const isNavActive = (href) => {
    if (href === '/about') return isAboutPage;
    if (href === '/team') return isTeamPage;
    if (href === '/contact') return isContactPage;
    if (href === '/blog') return onBlog;
    if (href === '/work') return isWorkPage;
    if (href === '/careers') return isCareersPage;
    if (href === '/services') return isServicesPage;
    if (href === '/services/software-development') return isSoftwareDevelopmentPage;
    if (href === '/services/ui-ux-design') return isUiUxDesignPage;
    return isNavItemActive(router.asPath, href);
  };
  const { fiUrl: contextFiUrl, enUrl: contextEnUrl } = useLanguageSwitcher();
  // FI link always goes to Finnish version of current page when one exists
  const isHomePath = router.pathname === '/' || router.pathname === HOME_FI || router.pathname === HOME_EN;
  const isAboutPath = router.asPath === ABOUT_EN || router.asPath === ABOUT_FI;
  const isTeamPath = router.asPath === TEAM_EN || router.asPath === TEAM_FI;
  const isContactPath = router.asPath === CONTACT_EN || router.asPath === CONTACT_FI;
  const isCareersPath = router.pathname === CAREERS_EN || router.pathname === CAREERS_FI;
  const isCareersSlugPath = router.pathname === '/rekry/[slug]' || router.pathname === '/careers/[slug]';
  const careersSlug = isCareersSlugPath && router.asPath ? router.asPath.replace(/^\/(rekry|careers)\//, '').split('?')[0].split('#')[0] : '';
  const isWorkPath = router.asPath === WORK_EN || router.asPath === WORK_FI;
  const isCaseStudyPath =
    router.pathname === '/en/work/[slug]' ||
    router.pathname === '/work/[slug]' ||
    router.pathname === '/tyomme/[slug]';
  let fiUrl;
  let enUrl;
  if (isHomePath) {
    fiUrl = HOME_FI;   // Etusivu
    enUrl = HOME_EN;   // /
  } else if (isAboutPath) {
    fiUrl = ABOUT_FI;
    enUrl = ABOUT_EN;
  } else if (isTeamPath) {
    fiUrl = TEAM_FI;
    enUrl = TEAM_EN;
  } else if (isContactPath) {
    fiUrl = CONTACT_FI;
    enUrl = CONTACT_EN;
  } else if (isCareersSlugPath && careersSlug) {
    fiUrl = `${CAREERS_FI}/${careersSlug}`;
    enUrl = `${CAREERS_EN}/${careersSlug}`;
  } else if (isCareersPath) {
    fiUrl = CAREERS_FI;
    enUrl = CAREERS_EN;
  } else if (isWorkPath) {
    fiUrl = WORK_FI;
    enUrl = WORK_EN;
  } else if (onBlog) {
    fiUrl = getBlogLanguageSwitchUrl(router.pathname, 'fi');
    enUrl = getBlogLanguageSwitchUrl(router.pathname, 'en');
  } else if (isCaseStudyPath) {
    fiUrl = contextFiUrl;
    enUrl = contextEnUrl;
  } else if (router.pathname === SERVICES_FI || router.pathname === '/palvelut') {
    fiUrl = SERVICES_FI;
    enUrl = SERVICES_EN;
  } else if (router.pathname === '/palvelut/ohjelmistokehitys') {
    fiUrl = `${SERVICES_FI}/ohjelmistokehitys`;
    enUrl = `${SERVICES_EN}/software-development`;
  } else if (router.pathname === '/palvelut/ui-ux-suunnittelu') {
    fiUrl = `${SERVICES_FI}/ui-ux-suunnittelu`;
    enUrl = `${SERVICES_EN}/ui-ux-design`;
  } else if (router.pathname === '/services' || router.pathname === '/services/software-development' || router.pathname === '/services/ui-ux-design') {
    fiUrl = router.pathname === '/services' ? SERVICES_FI : `${SERVICES_FI}/${router.pathname === '/services/software-development' ? 'ohjelmistokehitys' : 'ui-ux-suunnittelu'}`;
    enUrl = router.pathname === '/services' ? SERVICES_EN : `${SERVICES_EN}/${router.pathname === '/services/software-development' ? 'software-development' : 'ui-ux-design'}`;
  } else if (router.pathname === PRIVACY_FI || router.pathname === '/tietosuojaseloste') {
    fiUrl = PRIVACY_FI;
    enUrl = PRIVACY_EN;
  } else if (router.pathname === '/privacy-policy') {
    fiUrl = PRIVACY_FI;
    enUrl = PRIVACY_EN;
  } else if (router.pathname === '/[...wordpressNode]') {
    fiUrl = contextFiUrl;
    enUrl = contextEnUrl;
  } else {
    fiUrl = '/blogi';
    enUrl = '/blog';
  }
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const openServices = () => setServicesOpen(true);
  const closeServices = () => setServicesOpen(false);

  const handleServicesMouseEnter = () => {
    hoverTimeoutRef.current = window.setTimeout(openServices, HOVER_OPEN_DELAY_MS);
  };
  const handleServicesMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    closeServices();
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeServices();
      }
    }
    function handleEscape(e) {
      if (e.key === 'Escape') closeServices();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      if (hoverTimeoutRef.current) window.clearTimeout(hoverTimeoutRef.current);
    };
  }, []);


  return (
    <header
      className={`sticky top-0 z-50 w-full border-b ${
        useDarkHeader
          ? 'bg-navy border-white/10 max-lg:backdrop-blur-none lg:backdrop-blur-sm'
          : 'border-gray-100 max-lg:bg-white max-lg:backdrop-blur-none lg:bg-white/95 lg:backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4 min-w-0">
        <Link
          href={isFinnish ? HOME_FI : HOME_EN}
          className="flex items-center gap-3 shrink-0 transition-transform duration-200 hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-highlight focus-visible:ring-offset-[#0f172a] rounded"
        >
          <Image
            src="/identio-logo.png"
            alt="Identio"
            width={120}
            height={48}
            className={`h-12 w-auto ${useDarkHeader ? 'brightness-0 invert' : 'brightness-0'}`}
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-6">
          <nav className="flex items-center gap-1">
            {navLinks.map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className="relative services-menu"
                  ref={dropdownRef}
                  onMouseEnter={handleServicesMouseEnter}
                  onMouseLeave={handleServicesMouseLeave}
                >
                  <Link
                    href={getNavHref(item.href)}
                    className={`services-trigger flex items-center gap-1 px-4 py-2 text-sm font-medium uppercase tracking-wide rounded-md transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                      useDarkHeader
                        ? `focus-visible:ring-highlight focus-visible:ring-offset-[#0f172a] ${servicesOpen || isNavActive(item.href) ? 'text-highlight' : 'text-white/90 hover:text-highlight'}`
                        : `focus-visible:ring-brand ${servicesOpen || isNavActive(item.href) ? 'text-brand font-semibold' : 'text-[#1a1a1a] hover:text-brand'}`
                    }`}
                    aria-expanded={servicesOpen}
                    aria-haspopup="true"
                  >
                    {getNavLabel(item.href, item.label)}
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 transition-transform duration-300 ease-out ${servicesOpen ? 'rotate-180' : ''}`}
                    />
                  </Link>
                  {/* Dropdown: -mt-2 pt-2 extends hit area into gap so mouse can reach items without closing */}
                  <div
                    className={`absolute left-0 top-full z-50 -mt-2 pt-2 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
                      servicesOpen
                        ? 'visible translate-y-0 opacity-100 pointer-events-auto'
                        : 'invisible -translate-y-2.5 opacity-0 pointer-events-none'
                    }`}
                  >
                    <div className="relative min-w-[260px] rounded-xl bg-white py-2 shadow-[0_4px_6px_rgba(0,0,0,0.05),0_10px_25px_rgba(0,0,0,0.1)]">
                      {/* Triangle pointer */}
                      <div
                        className="absolute -top-1.5 left-6 h-3 w-3 rotate-45 bg-white shadow-[0_-2px_4px_rgba(0,0,0,0.05)]"
                        aria-hidden
                      />
                      {item.children.map((child, idx) => (
                      <Link
                        key={child.href}
                        href={getNavHref(child.href)}
                        className={`services-dropdown-item group flex items-center gap-2 px-5 py-3 text-[15px] font-medium text-[#4b5563] transition-all duration-200 first:rounded-t-xl last:rounded-b-xl hover:bg-[#f9fafb] hover:pl-6 hover:text-[#0f172a] ${
                          idx === 0 ? 'rounded-t-xl' : ''
                        } ${idx === item.children.length - 1 ? 'rounded-b-xl' : ''}`}
                        onClick={closeServices}
                      >
                        <span className="w-4 shrink-0 text-brand opacity-0 transition-opacity duration-200 group-hover:opacity-100" aria-hidden>
                          →
                        </span>
                        <span className="whitespace-nowrap">{getNavLabel(child.href, child.label)}</span>
                      </Link>
                    ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.label}
                  href={getNavHref(item.href)}
                  className={`nav-link px-4 py-2 text-sm font-medium uppercase tracking-wide rounded-md transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    isNavActive(item.href)
                      ? useDarkHeader
                        ? 'text-highlight focus-visible:ring-highlight focus-visible:ring-offset-[#0f172a]'
                        : 'text-brand font-semibold focus-visible:ring-brand'
                      : useDarkHeader
                        ? 'text-white/90 hover:text-highlight focus-visible:ring-highlight focus-visible:ring-offset-[#0f172a]'
                        : 'text-[#1a1a1a] hover:text-brand focus-visible:ring-brand'
                  }`}
                >
                  {getNavLabel(item.href, item.label)}
                </Link>
              )
            )}
          </nav>
          <div className={`flex items-center gap-4 pl-4 border-l ${useDarkHeader ? 'border-white/20' : 'border-gray-200'}`}>
            <Link
              href={fiUrl}
              className={`text-sm font-medium transition-colors ${isFinnish ? (useDarkHeader ? 'text-highlight' : 'text-brand') : useDarkHeader ? 'text-white/70 hover:text-highlight' : 'text-[#6b7280] hover:text-brand'}`}
            >
              FI
            </Link>
            <Link
              href={enUrl}
              className={`text-sm font-medium transition-colors ${!isFinnish ? (useDarkHeader ? 'text-highlight' : 'text-brand') : useDarkHeader ? 'text-white/70 hover:text-highlight' : 'text-[#6b7280] hover:text-brand'}`}
            >
              EN
            </Link>
          </div>
        </div>

        {/* Mobile menu button – hamburger animates to X */}
        <button
          type="button"
          className={`lg:hidden w-12 h-12 flex flex-col items-center justify-center gap-1.5 transition-colors duration-200 ${
            mobileOpen
              ? (useDarkHeader ? 'text-highlight' : 'text-brand')
              : (useDarkHeader ? 'text-white' : 'text-[#1a1a1a]')
          }`}
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          <span
            className={`block h-0.5 w-6 origin-center bg-current transition-all duration-300 ease-out ${
              mobileOpen ? 'translate-y-[7px] rotate-45' : ''
            }`}
          />
          <span
            className={`block h-0.5 w-6 bg-current transition-all duration-200 ${
              mobileOpen ? 'scale-x-0 opacity-0' : 'scale-x-100 opacity-100'
            }`}
          />
          <span
            className={`block h-0.5 w-6 origin-center bg-current transition-all duration-300 ease-out ${
              mobileOpen ? '-translate-y-[7px] -rotate-45' : ''
            }`}
          />
        </button>
      </div>

      {/* Mobile nav – Reaktor-inspired staggered reveal */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            variants={mobilePanelVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className={`lg:hidden border-t overflow-hidden ${
              useDarkHeader ? 'border-white/10 bg-[#0f172a]' : 'border-gray-100 bg-white'
            }`}
          >
            <motion.nav
              variants={mobileNavVariants}
              className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-0 min-w-0"
            >
              {navLinks.map((item) =>
                item.children ? (
                  <Fragment key={item.label}>
                    <motion.div variants={mobileItemVariants}>
                      <Link
                        href={getNavHref(item.href)}
                        className={`block px-3 py-3 rounded-md font-medium min-h-[44px] flex items-center transition-colors ${
                          isNavActive(item.href)
                            ? useDarkHeader ? 'text-highlight' : 'text-brand font-semibold'
                            : useDarkHeader ? 'text-white/90 hover:text-highlight' : 'text-[#1a1a1a] hover:text-brand'
                        }`}
                        onClick={() => setMobileOpen(false)}
                      >
                        {getNavLabel(item.href, item.label)}
                      </Link>
                    </motion.div>
                    {item.children.map((child) => (
                      <motion.div key={child.href} variants={mobileItemVariants}>
                        <Link
                          href={getNavHref(child.href)}
                          className={`block pl-6 pr-3 py-3 min-h-[44px] flex items-center rounded-md text-[15px] ${
                            useDarkHeader ? 'text-white/80 hover:text-highlight' : 'text-[#1a1a1a] hover:text-brand'
                          }`}
                          onClick={() => setMobileOpen(false)}
                        >
                          {getNavLabel(child.href, child.label)}
                        </Link>
                      </motion.div>
                    ))}
                  </Fragment>
                ) : (
                  <motion.div variants={mobileItemVariants}>
                    <Link
                      href={getNavHref(item.href)}
                      onClick={() => setMobileOpen(false)}
                      className={`block px-3 py-3 rounded-md font-medium min-h-[44px] flex items-center transition-colors ${
                        isNavActive(item.href)
                          ? useDarkHeader ? 'text-highlight' : 'text-brand font-semibold'
                          : useDarkHeader ? 'text-white/90 hover:text-highlight' : 'text-[#1a1a1a] hover:text-brand'
                      }`}
                    >
                      {getNavLabel(item.href, item.label)}
                    </Link>
                  </motion.div>
                )
              )}
              <motion.div
                variants={mobileItemVariants}
                className={`flex gap-3 pt-4 mt-1 pb-4 border-t justify-center ${useDarkHeader ? 'border-white/10' : 'border-gray-100'}`}
              >
                <Link
                  href={fiUrl}
                  onClick={() => setMobileOpen(false)}
                  className={`min-h-[48px] min-w-[48px] flex-1 max-w-[140px] flex items-center justify-center rounded-lg border text-sm font-medium transition-colors ${
                    isFinnish
                      ? useDarkHeader ? 'border-highlight/50 text-highlight' : 'border-brand/50 text-brand'
                      : useDarkHeader ? 'border-white/20 text-white/70 hover:text-highlight' : 'border-gray-200 text-[#6b7280] hover:text-brand hover:border-gray-300'
                  }`}
                >
                  FI
                </Link>
                <Link
                  href={enUrl}
                  onClick={() => setMobileOpen(false)}
                  className={`min-h-[48px] min-w-[48px] flex-1 max-w-[140px] flex items-center justify-center rounded-lg border text-sm font-medium transition-colors ${
                    !isFinnish
                      ? useDarkHeader ? 'border-highlight/50 text-highlight' : 'border-brand/50 text-brand'
                      : useDarkHeader ? 'border-white/20 text-white/70 hover:text-highlight' : 'border-gray-200 text-[#1a1a1a] hover:text-brand hover:border-gray-300'
                  }`}
                >
                  EN
                </Link>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
